import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { PAGE_SIZE_OPTIONS } from '../../constant/constant';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { saveAs } from 'file-saver';
import { AnonymousFeedbackService, AnonymousFeedbackResponse } from '../../services/anonymous-feedback.service';
import { ResponseBase } from '../../constant/response';
import { ExportExcel } from '../../constant/ExcelExport';
import { PageResponseDTO } from '../../dtos/PageResponseDTO';
import { RoleEnum } from '../../enums/role.enum';


@Component({
  selector: 'app-anonymous-feedback-list',
  templateUrl: './anonymous-feedback-list.component.html',
  styleUrl: './anonymous-feedback-list.component.css',
  standalone: false
})
export class AnonymousFeedbackListComponent implements OnInit {
  totalItemsCount: number = 0;
  displayedColumns: string[] = ['submittedOn', 'feedback'];
  pageSize = 10;
  pageSizeOptions = PAGE_SIZE_OPTIONS;
  currentPage = 0;
  dataSource = new MatTableDataSource<AnonymousFeedbackResponse>([]);
  isLoading = false;
  isAdmin = false;
  isMyFeedback = false;
  isExcelExporting = false;
  userId: number | null = null;

  constructor(
    private route: ActivatedRoute,
    private feedbackService: AnonymousFeedbackService,
    private snackBar: MatSnackBar,
    private exportData: ExportExcel
  ) {}

  ngOnInit(): void {
    this.isMyFeedback = this.route.snapshot.routeConfig?.path === 'anonymous-feedback/my';

    if (typeof sessionStorage !== 'undefined') {
      this.isAdmin = Number(sessionStorage.getItem('roleId')) === RoleEnum.ADMIN;
      const userData = sessionStorage.getItem('userData');
      this.userId = userData ? JSON.parse(userData)?.id ?? null : null;
    }

    if (this.isMyFeedback) {
      this.getMyFeedback();
      return;
    }

    if (!this.isAdmin) {
      this.snackBar.open('Only admin can view feedback list.', 'Close', { duration: 3000 });
      return;
    }

    this.getFeedbackList();
  }

  onPageChange(event: PageEvent) {
    console.log(this.currentPage, this.pageSize);
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.getFeedbackList();
    this.getMyFeedback;
  }

  getFeedbackList(): void {
    this.isLoading = true;
    this.feedbackService.list(this.currentPage , this.pageSize).subscribe({
      next: (res: ResponseBase<PageResponseDTO<AnonymousFeedbackResponse>>) => {
        this.dataSource.data = res.responseObject.responseData;
        this.totalItemsCount = res.responseObject.totalCount;
        this.isLoading = false;
      },
      error: (err) => {
        this.snackBar.open(err?.message || 'Failed to load feedback.', 'Close', { duration: 3000 });
        this.isLoading = false;
      }
    });
  }

  getMyFeedback(): void {
    if (!this.userId) {
      this.snackBar.open('User data not found.', 'Close', { duration: 3000 });
      return;
    }

    this.isLoading = true;
    this.feedbackService.myFeedback(this.userId).subscribe({
      next: (res: ResponseBase<AnonymousFeedbackResponse[]>) => {
        this.dataSource.data = res.responseObject || [];
        this.isLoading = false;
      },
      error: (err) => {
        this.snackBar.open(err?.message || 'Failed to load feedback.', 'Close', { duration: 3000 });
        this.isLoading = false;
      }
    });
  }

  exportFeedback(): void {
    if (!this.isAdmin || this.isMyFeedback) {
      this.snackBar.open('Only admin can export feedback.', 'Close', { duration: 3000 });
      return;
    }

    this.isExcelExporting = true;
    this.feedbackService.export().subscribe({
      next: (res: ResponseBase<string>) => {
        if (res.responseObject) {
          saveAs(this.exportData.base64ToBlob(res.responseObject), `anonymousFeedback_${this.getFormattedDate()}`);
          this.snackBar.open('Feedback exported successfully.', 'Close', { duration: 3000 });
        } else {
          this.snackBar.open(res.message || 'No feedback found.', 'Close', { duration: 3000 });
        }
        this.isExcelExporting = false;
      },
      error: (err) => {
        this.snackBar.open(err?.message || 'Failed to export feedback.', 'Close', { duration: 3000 });
        this.isExcelExporting = false;
      }
    });
  }

  private getFormattedDate(): string {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year}_${hours}-${minutes}.xlsx`;
  }

}
